import React, { useContext, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';

import { PERMISSION, SERVER_DATE_FORMAT } from 'constants/logic';
import ScheduleFilterChips, { SCHEDULE_FILTER_KEY, getDefaultScheduleFilterKey } from 'features/ScheduleFilters';
import { userHasPermission } from 'helpers/core';
import Segment from 'helpers/segment';
import { selectActiveVenue, selectSchedulesStartDate, selectUserPermissions } from 'selectors/general';

import VenueSchedulesContext from './Contexts/VenueSchedulesContext';
import { setScheduleListFilter } from './YieldManagement.reducers';
import { selectScheduleListFilter } from './YieldManagement.selectors';

const SCHEDULE_FILTER_KEY_VALUES = Object.values(SCHEDULE_FILTER_KEY);
const SEGMENT_SCHEDULE_FILTER_CLICKED = 'Schedule Filter clicked';

function ScheduleFilterChipsContainer() {
  const dispatch = useDispatch();
  const selectedKey = useSelector(selectScheduleListFilter);
  const schedulesStartDate = useSelector(selectSchedulesStartDate);
  const venue = useSelector(selectActiveVenue);
  const permissions = useSelector(selectUserPermissions);
  const { filterCounts } = useContext(VenueSchedulesContext);

  const { isManualStudio, venueId } = venue;
  const hasSpotManagePermission = userHasPermission({ permissions }, PERMISSION.DISPLAY_SPOT_MANAGEMENT);
  const dayKey = `${venueId}-${format(schedulesStartDate, SERVER_DATE_FORMAT)}`;
  const previousDayKeyRef = useRef(dayKey);
  const defaultKey = getDefaultScheduleFilterKey(filterCounts || {}, isManualStudio);
  const selectedKeyForChips = SCHEDULE_FILTER_KEY_VALUES.includes(selectedKey)
    ? selectedKey
    : defaultKey || SCHEDULE_FILTER_KEY.ACTIVE_CLASSES;

  useEffect(() => {
    if (!defaultKey) {
      previousDayKeyRef.current = dayKey;
      return;
    }

    const dayChanged = previousDayKeyRef.current !== dayKey;
    previousDayKeyRef.current = dayKey;
    const selectedKeyIsV2 = SCHEDULE_FILTER_KEY_VALUES.includes(selectedKey);

    if (dayChanged || !selectedKeyIsV2) {
      dispatch(setScheduleListFilter(defaultKey));
    }
  }, [dayKey, defaultKey, dispatch, selectedKey]);

  function handleChange(filterKey) {
    Segment.event(SEGMENT_SCHEDULE_FILTER_CLICKED, {
      filterType: filterKey.toLowerCase(),
      venueId,
    });
    dispatch(setScheduleListFilter(filterKey));
  }

  return (
    <ScheduleFilterChips
      counts={filterCounts}
      hasSpotManagePermission={hasSpotManagePermission}
      isManualStudio={isManualStudio}
      selectedKey={selectedKeyForChips}
      onChange={handleChange}
    />
  );
}

export default ScheduleFilterChipsContainer;
